import type { FakeRouteConfig } from 'vite-plugin-fake-server/client';

import {
  type PlatformPermission,
  platformPermissions,
} from '@/services/business/permissions';
import { accountStore } from '../store/account';
import { resultError } from './utils';

type FakeRoute = Exclude<FakeRouteConfig, FakeRouteConfig[]>;

function permissionForRoute(
  method: string,
  url: string,
): PlatformPermission | undefined {
  const routeMethod = method.toLowerCase();
  if (url === '/platform/api-keys' || url.startsWith('/platform/api-keys/')) {
    if (routeMethod === 'get') return platformPermissions.apiKeysRead;
    if (url.endsWith('/revoke')) return platformPermissions.apiKeysRevoke;
    return routeMethod === 'post'
      ? platformPermissions.apiKeysCreate
      : platformPermissions.apiKeysUpdate;
  }

  if (url.startsWith('/platform/upstream-account-connections')) {
    return platformPermissions.accountsConnect;
  }
  if (
    url === '/platform/upstream-accounts' ||
    url.startsWith('/platform/upstream-accounts/')
  ) {
    return routeMethod === 'get'
      ? platformPermissions.accountsRead
      : platformPermissions.accountsUpdate;
  }

  if (url === '/platform/instances' || url.startsWith('/platform/instances/')) {
    return platformPermissions.instancesRead;
  }

  if (url === '/platform/servers' || url.startsWith('/platform/servers/')) {
    return routeMethod === 'get'
      ? platformPermissions.serversRead
      : url === '/platform/servers'
        ? platformPermissions.serversCreate
        : platformPermissions.serversUpdate;
  }

  if (
    url === '/platform/dashboard/statistics' ||
    url === '/platform/dashboard/operations'
  ) {
    return platformPermissions.dashboardRead;
  }

  if (url === '/system/info') {
    return platformPermissions.aboutRead;
  }

  if (url.startsWith('/platform/users')) {
    if (routeMethod === 'get') return platformPermissions.usersRead;
    if (url === '/platform/users' && routeMethod === 'post') {
      return platformPermissions.usersCreate;
    }
    if (routeMethod === 'delete' && !url.includes('/roles/')) {
      return platformPermissions.usersDelete;
    }
    return platformPermissions.usersUpdate;
  }

  if (url.startsWith('/platform/roles')) {
    if (routeMethod === 'get') return platformPermissions.rolesRead;
    if (url === '/platform/roles' && routeMethod === 'post') {
      return platformPermissions.rolesCreate;
    }
    if (routeMethod === 'delete' && !url.includes('/permissions/')) {
      return platformPermissions.rolesDelete;
    }
    return platformPermissions.rolesUpdate;
  }

  if (url.startsWith('/platform/departments')) {
    if (routeMethod === 'get') return platformPermissions.departmentsRead;
    if (routeMethod === 'post') return platformPermissions.departmentsCreate;
    if (routeMethod === 'delete') return platformPermissions.departmentsDelete;
    return platformPermissions.departmentsUpdate;
  }

  if (url.startsWith('/platform/positions')) {
    if (routeMethod === 'get') return platformPermissions.positionsRead;
    if (routeMethod === 'post') return platformPermissions.positionsCreate;
    if (routeMethod === 'delete') return platformPermissions.positionsDelete;
    return platformPermissions.positionsUpdate;
  }

  if (url.startsWith('/platform/dictionaries')) {
    if (routeMethod === 'get') return platformPermissions.dictionariesRead;
    if (routeMethod === 'post') return platformPermissions.dictionariesCreate;
    if (routeMethod === 'delete') return platformPermissions.dictionariesDelete;
    return platformPermissions.dictionariesUpdate;
  }

  if (url.startsWith('/platform/announcements')) {
    if (routeMethod === 'get') return platformPermissions.announcementsRead;
    if (routeMethod === 'post') return platformPermissions.announcementsCreate;
    if (url === '/platform/announcements/status') {
      return platformPermissions.announcementsBatch;
    }
    if (routeMethod === 'delete' && url === '/platform/announcements') {
      return platformPermissions.announcementsBatch;
    }
    if (routeMethod === 'delete') return platformPermissions.announcementsDelete;
    return platformPermissions.announcementsUpdate;
  }

  if (url === '/platform/audit-logs' || url === '/platform/login-logs') {
    return platformPermissions.logsRead;
  }

  if (url === '/platform/settings' || url.startsWith('/platform/security')) {
    return routeMethod === 'get'
      ? platformPermissions.settingsRead
      : platformPermissions.settingsUpdate;
  }
}

function protectRoute(route: FakeRoute): FakeRoute {
  const permission = permissionForRoute(route.method ?? 'get', route.url);
  if (!route.response) return route;

  const response = route.response;
  return {
    ...route,
    response: (request, incomingMessage, serverResponse) => {
      if (!accountStore.currentUser().isLogin) {
        return resultError('Unauthorized', 401);
      }
      try {
        if (permission && !accountStore.hasPermission(permission)) {
          return resultError('Forbidden', 403);
        }
      } catch {
        return resultError('Unauthorized', 401);
      }
      return response(request, incomingMessage, serverResponse);
    },
  };
}

export function protectBusinessRoutes(routes: FakeRouteConfig): FakeRouteConfig {
  if (Array.isArray(routes)) return routes.map(protectRoute);
  return protectRoute(routes);
}
